/**
 * 火山方舟 Seedance 1.5 Pro 视频（官方任务接口，独立 baseURL + API Key）
 */
import axios from 'axios'
import { extractApiErrorMessage } from '../../extract-api-error-message'
import { proxifyVolcArkTosFetchUrl } from '../volc-ark-tos-fetch-url'

var POLL_INTERVAL = 3000
var POLL_TIMEOUT = 10 * 60 * 1000
var TASK_PATH = '/contents/generations/tasks'

export function getVolcSeedanceVideoConfig() {
  return {
    baseURL: (process.env.VUE_APP_OFFICIAL_VOLC_SEEDANCE_VIDEO_BASE_URL || '').replace(/\/$/, ''),
    apiKey: process.env.VUE_APP_OFFICIAL_VOLC_SEEDANCE_VIDEO_API_KEY || '',
    model: process.env.VUE_APP_OFFICIAL_VOLC_SEEDANCE_MODEL || 'doubao-seedance-1-5-pro-251215'
  }
}

function createClient() {
  var cfg = getVolcSeedanceVideoConfig()
  if (!cfg.baseURL) {
    throw new Error('未配置 VUE_APP_OFFICIAL_VOLC_SEEDANCE_VIDEO_BASE_URL')
  }
  if (!cfg.apiKey) {
    throw new Error('未配置 VUE_APP_OFFICIAL_VOLC_SEEDANCE_VIDEO_API_KEY')
  }
  var instance = axios.create({
    baseURL: cfg.baseURL,
    timeout: 120000,
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + cfg.apiKey
    }
  })
  instance.interceptors.response.use(
    function (res) {
      return res.data
    },
    function (err) {
      var msg =
        extractApiErrorMessage(err.response && err.response.data) ||
        err.message ||
        '请求失败'
      return Promise.reject(new Error(msg))
    }
  )
  return { instance: instance, model: cfg.model }
}

/**
 * @param {Object} params - 与 Pollo 统一的视频参数
 * @returns {Promise<{ taskId: string }>}
 */
export function submitVolcSeedanceTask(params) {
  var c = createClient()
  var content = [{ type: 'text', text: (params.prompt || '').trim().slice(0, 2000) }]
  var ref = params.referenceImage && String(params.referenceImage).trim()
  if (ref) {
    content.push({ type: 'image_url', image_url: { url: ref }, role: 'first_frame' })
  }
  var end = params.endFrameImage && String(params.endFrameImage).trim()
  if (end) {
    content.push({ type: 'image_url', image_url: { url: end }, role: 'last_frame' })
  }

  var duration = parseInt(params.videoDuration, 10)
  if (isNaN(duration)) duration = 5
  duration = Math.min(10, Math.max(4, duration))

  var resolution = params.videoRes === '480p' || params.videoRes === '720p' ? params.videoRes : '720p'
  var ratio = ['16:9', '9:16', '1:1'].indexOf(params.aspectRatio) !== -1 ? params.aspectRatio : '16:9'

  var seed = params.seed
  if (seed === '' || seed == null) seed = -1
  seed = parseInt(seed, 10)
  if (isNaN(seed)) seed = -1
  if (seed < -1) seed = -1
  var maxSeed = Math.pow(2, 32) - 1
  if (seed > maxSeed) seed = maxSeed

  var body = {
    model: c.model,
    content: content,
    ratio: ratio,
    duration: duration,
    resolution: resolution,
    watermark: false,
    seed: seed,
    generate_audio: params.generateAudio !== false
  }

  return c.instance.post(TASK_PATH, body).then(function (data) {
    var id = data && (data.id || data.task_id)
    if (!id) return Promise.reject(new Error('官方接口未返回任务 id'))
    return { taskId: String(id) }
  })
}

function parseTaskStatus(data) {
  if (!data) {
    return { status: 'failed', error: '空响应' }
  }
  var st = String(data.status || '').toLowerCase()
  var msg = extractApiErrorMessage(data) || (data.error && (typeof data.error === 'string' ? data.error : data.error.message)) || ''

  if (st === 'succeeded') {
    var url = data.content && data.content.video_url ? String(data.content.video_url) : ''
    if (!url) return { status: 'failed', error: msg || '未返回视频地址' }
    return { status: 'completed', url: url }
  }

  if (st === 'failed' || st === 'expired' || st === 'cancelled') {
    return { status: 'failed', error: msg || (st === 'expired' ? '任务超时' : st === 'cancelled' ? '任务已取消' : '生成失败') }
  }

  if (st === 'queued' || st === 'running') {
    return { status: 'processing', url: null, error: null }
  }

  return { status: 'processing', url: null, error: null }
}

function downloadVideoBlob(videoUrl) {
  var fetchUrl = proxifyVolcArkTosFetchUrl(videoUrl)
  return fetch(fetchUrl, fetchUrl.indexOf('/') === 0 ? {} : { mode: 'cors' })
    .then(function (res) {
      if (!res.ok) return Promise.reject(new Error('download video failed: HTTP ' + res.status))
      return res.blob()
    })
    .then(function (blob) {
      if (!blob || !blob.size) return Promise.reject(new Error('download video failed: empty blob'))
      return blob
    })
}

/**
 * @returns {{ stop: function(): void }}
 */
export function pollVolcSeedanceTask(taskId, callback, interval) {
  var pollInterval = interval || POLL_INTERVAL
  var deadline = Date.now() + POLL_TIMEOUT
  var pollTimer = null
  var stopped = false
  var client = createClient().instance
  var path = TASK_PATH + '/' + encodeURIComponent(taskId)

  function stop() {
    stopped = true
    if (pollTimer) {
      clearTimeout(pollTimer)
      pollTimer = null
    }
  }

  function poll() {
    if (stopped) return
    client
      .get(path)
      .then(function (data) {
        if (stopped) return
        var parsed = parseTaskStatus(data)
        if (parsed.status === 'completed' && parsed.url) {
          downloadVideoBlob(parsed.url)
            .then(function (blob) {
              parsed.videoBlob = blob
              if (typeof callback === 'function') callback(null, parsed)
              stop()
            })
            .catch(function (dlErr) {
              console.warn('[Seedance] download blob failed, using url only', dlErr)
              if (typeof callback === 'function') callback(null, parsed)
              stop()
            })
          return
        }
        if (typeof callback === 'function') {
          callback(null, parsed)
        }
        if (parsed.status === 'failed') {
          stop()
          return
        }
        if (Date.now() > deadline) {
          if (typeof callback === 'function') callback(new Error('轮询超时'), null)
          stop()
          return
        }
        pollTimer = setTimeout(poll, pollInterval)
      })
      .catch(function (err) {
        if (stopped) return
        if (typeof callback === 'function') callback(err, null)
        stop()
      })
  }

  poll()
  return { stop: stop }
}
